/**
 * CreditTopUpContext — hosts the single universal "add credits" popup and the
 * imperative API to open it from anywhere.
 *
 * Two ways the popup opens:
 *  1. Automatically — registers a global handler with the axios layer
 *     (setCreditExhaustedHandler) so ANY gateway call returning a 402
 *     `insufficient_credits` opens the popup with no per-page wiring.
 *  2. Manually — components call `useCreditTopUp().openTopUp(detail)` (e.g. a
 *     proactive "low balance" prompt before an expensive action).
 *
 * Mounted once near the top of the app tree (App.tsx), inside the router and
 * Ant Design App contexts. Contract: docs/product/credit-actions-model.md.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { CreditTopUpContext } from './CreditTopUpContext.context';
import {
  setCreditExhaustedHandler,
  type InsufficientCreditsDetail,
} from '@/api/client';
import CreditTopUpModal from '@/components/credits/CreditTopUpModal';

export const CreditTopUpProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<InsufficientCreditsDetail | null>(null);

  const openTopUp = useCallback((next?: InsufficientCreditsDetail) => {
    setDetail(next ?? null);
    setOpen(true);
  }, []);

  const closeTopUp = useCallback(() => {
    setOpen(false);
  }, []);

  // Register/deregister the global 402 handler so any insufficient_credits
  // response opens this popup.
  useEffect(() => {
    setCreditExhaustedHandler((next) => openTopUp(next));
    return () => setCreditExhaustedHandler(null);
  }, [openTopUp]);

  const value = useMemo(
    () => ({ openTopUp, closeTopUp, isOpen: open }),
    [openTopUp, closeTopUp, open],
  );

  return (
    <CreditTopUpContext.Provider value={value}>
      {children}
      <CreditTopUpModal open={open} detail={detail} onClose={closeTopUp} />
    </CreditTopUpContext.Provider>
  );
};

// The `useCreditTopUp` hook lives in ./CreditTopUpContext.hooks so this file
// only exports the Provider component (react-refresh / fast-refresh contract).
